import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { AltSoftPullConsentAlert, MandatoryDetailsCard, useMandatoryDetailsSubmit } from '@makemydeal/dr-dash-components';
import * as customerSpdSelectors from '@makemydeal/dr-dash-store/selectors/customerSpdSelectors';
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import {
    altDealStructureRetryRatesClicked,
    softpullAltConsentContinueClicked,
    softpullMandatoryDetailsContinueClicked
} from '@makemydeal/dr-dash-store/actions/deskingActionCreators';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';

import { SlideoutContainer, Subheader, SlideoutBody } from './AiAltDealStructureSlideout.style';
import { AltDealStructurePrimaryCard } from './AltDealStructurePrimaryCard';
import { FinanceStructuresGrid } from './FinanceStructuresGrid';
import { LeaseStructuresGrid } from './LeaseStructuresGrid';
import { AltDealStructureOptionsViewTracker } from './AltDealStructureOptionsViewTracker';
import { buildDealStructurePredictionsFromSlots } from './altDealStructureCdlUtils';
import { FailureAlertBanner } from './FailureAlertBanner';
import { ScenarioLimitAlert } from './ScenarioLimitAlert';
import {
    useGridData,
    useGridActions,
    getUnavailableTermColumns,
    getUnavailableEditedDownPaymentRows,
    isRetryableFailedSlot
} from './contexts';

export const AiAltDealStructureSlideoutContent = () => {
    const theme = useInterstateTheme();
    const dispatch = useDispatch();

    const { slots } = useGridData();
    const { retry, isAtMaxScenarios, editedDownPaymentKeys } = useGridActions();
    const vehicleAnalyticsPayload = useSelector(vehicleSelectors.getVehicleAnalyticsPayload);

    const failedBatchKeys = useMemo(() => {
        // Exclude failures from unavailable (invalid) term columns and user-entered invalid down-payment
        // rows — retrying won't help, and the inputs already flag them. Keeps Retry Rates for
        // genuinely retryable rows/cells (incl. transient whole-row failures the user didn't type).
        const unavailableColumns = getUnavailableTermColumns(slots);
        const unavailableRows = getUnavailableEditedDownPaymentRows(slots, editedDownPaymentKeys);
        return [
            ...new Set(slots.filter((s) => isRetryableFailedSlot(s, unavailableColumns, unavailableRows)).map((s) => s.batchKey))
        ];
    }, [slots, editedDownPaymentKeys]);

    // Suppress the failure/retry banner while the grid is still streaming
    const isLoading = useMemo(() => slots.some((s) => s.status === 'pending'), [slots]);

    // Only wired to the failure banner, which renders solely when failedBatchKeys is non-empty.
    const retryAll = useCallback(() => {
        // dc-mv-296: fire once per click with all presented (ready) structures; the per-batch retries below
        // are mechanical only, so a multi-row retry emits a single event rather than one per row.
        dispatch(altDealStructureRetryRatesClicked(vehicleAnalyticsPayload, buildDealStructurePredictionsFromSlots(slots)));
        for (const batchKey of failedBatchKeys) {
            retry(batchKey);
        }
    }, [failedBatchKeys, retry, dispatch, vehicleAnalyticsPayload, slots]);

    const softPullConsentProvided = useSelector(offerInfoSelectors.getSoftPullConsentProvided);
    const hasIdentityDataCompleteness = useSelector(customerSpdSelectors.getHasIdentityDataCompleteness);
    const isEnableSoftPullConsentMV = useSelector(featureToggleSelectors.isEnableSoftPullConsentMV);
    const isEnableSoftPullConsentWithoutDLMV = useSelector(featureToggleSelectors.isEnableSoftPullConsentWithoutDLMV);
    const isSpdManagementEnabled = useSelector(dealerSelectors.isSpdManagementEnabled);

    const shouldShowConsentAlert = isEnableSoftPullConsentMV && softPullConsentProvided === false && hasIdentityDataCompleteness;
    // State 1 (Mandatory Details) is the "soft-pull without DL" path — gate on the no-DL toggle so
    // we preserve the existing master-switch semantic: toggle OFF + no DL = no soft-pull UI at all.
    // softPullConsentProvided check uses !== true (not === false) because the modal that flips it
    // to false only opens when identity is complete; State 1 fires on the inverse, so the value
    // stays undefined on fresh deals.
    const shouldShowMandatoryDetails =
        isEnableSoftPullConsentMV &&
        softPullConsentProvided !== true &&
        !hasIdentityDataCompleteness &&
        isSpdManagementEnabled &&
        isEnableSoftPullConsentWithoutDLMV;

    // Lease only applies to NEW vehicles (pre-owned — used/CPO — hides lease regardless of returned rates)
    // and only when the dealer has leasing enabled in DR Admin. Otherwise hide the whole lease section,
    // header included.
    const isNewVehicle = useSelector(vehicleSelectors.isVehicleNew);
    const hasLeasing = useSelector(dealerSelectors.hasLeasing);
    const shouldShowLeaseGrid = isNewVehicle && hasLeasing;

    const handleConsentAccept = () => {
        dispatch(softpullAltConsentContinueClicked(vehicleAnalyticsPayload)); // dc-mv-281: Analytics tag
        dispatch(offerActionCreators.setSoftPullConsentProvided(true));
    };

    const {
        initialValues: mandatoryDetailsInitialValues,
        onSubmit: handleMandatoryDetailsSubmit,
        isSubmitting: isMandatoryDetailsSubmitting
    } = useMandatoryDetailsSubmit();

    return (
        <SlideoutContainer data-testid="ai-alt-deal-structure-slideout-content">
            <Subheader variant="body-md" theme={theme} data-testid="ai-alt-deal-structure-slideout-subheader">
                AI-recommended deal structures for the same vehicle. <strong>You can create up to two additional scenarios.</strong>
            </Subheader>

            {shouldShowMandatoryDetails && (
                <MandatoryDetailsCard
                    initialValues={mandatoryDetailsInitialValues}
                    onSubmit={(values) => {
                        dispatch(softpullMandatoryDetailsContinueClicked(vehicleAnalyticsPayload)); // dc-mv-300: Analytics tag
                        handleMandatoryDetailsSubmit(values);
                    }}
                    isSubmitting={isMandatoryDetailsSubmitting}
                />
            )}
            {shouldShowConsentAlert && <AltSoftPullConsentAlert onAccept={handleConsentAccept} />}

            <SlideoutBody>
                <AltDealStructurePrimaryCard />

                {isAtMaxScenarios && <ScenarioLimitAlert />}

                {!isLoading && failedBatchKeys.length > 0 && <FailureAlertBanner onRetryAll={retryAll} isRetryDisabled={false} />}

                <FinanceStructuresGrid />

                {shouldShowLeaseGrid && <LeaseStructuresGrid />}

                {/* dc-mv-289: single view event for all presented (finance + lease) structures, once settled. */}
                <AltDealStructureOptionsViewTracker />
            </SlideoutBody>
        </SlideoutContainer>
    );
};
